import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { db } from "../../../../../../firebase";
import { set, ref, get } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Typography,
  Modal,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Stack,
  useTheme,
  Autocomplete,
  useMediaQuery,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import AddIcon from "@mui/icons-material/Add";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Formik } from "formik";
import * as yup from "yup";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";

import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { getTokenRefresh } from "../../../../../../helpers/token/refreshToken";

import "../../style/booking.css";
import {
  boxModalStyle,
  circularProgressStyle,
  suggestionBackgroundColor,
} from "../../style/BookingStyle";
import { getAddressAndLatLng } from "../../../../../../api/resources/url/expandUrl";
import SnackBar from "../../../../../../components/snackbar/SnackBar";
import { tokens } from "../../../../../../theme";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { getClients } from "../../../../../../api/users/client/client";
import { dateFormat, timeFormat } from "../../../../../../helpers/date/date";
import { sendICSEmail } from "../../../../../../api/resources/ics/ics";
import { getListCountryUseCase } from "../../../tour/usecases/country/GetListCountryUseCase";
import {
  getListTourAll,
  getListTourByCountry,
} from "../../../tour/data/repository/tourRepo";
import { sendBookingRiderNotificationUseCase } from "../../../../../../features/booking/riderNotification/domain/usecases/sendBookingRiderNotificationUseCase";
import { CreateBackupUseCase } from "../../../backup/usecases/CreateBackupOrder";
import { getClientCityRangeListUseCase } from "../../../../../../features/tarifas/client/city_range/domain/usecases/getClientCityRangeListUseCase";

const GOOGLE_MAPS_API_KEY = "AIzaSyBIbtiiqiEJ6F2ScRafjfv1SmpaeWg3NDQ";
setOptions({ key: GOOGLE_MAPS_API_KEY, libraries: ["routes"] });

function getPrecioKmTempByDistance(distanceKm, cityRangeRates) {
  const matchingRange = cityRangeRates.find((range) => {
    const minKm = Number(range.min_km);
    const maxKm = Number(range.max_km);

    return (
      !Number.isNaN(minKm) &&
      !Number.isNaN(maxKm) &&
      distanceKm > minKm &&
      distanceKm < maxKm
    );
  });

  if (!matchingRange || Number.isNaN(Number(matchingRange.price_km))) {
    return null;
  }

  return Number(matchingRange.price_km);
}

function roundToNearestFive(value) {
  return Math.round(value / 5) * 5;
}

async function getGoogleRouteDistanceKm(origin, destination) {
  const { DirectionsService, DirectionsStatus, TravelMode } =
    await importLibrary("routes");

  return new Promise((resolve) => {
    const directionsService = new DirectionsService();

    directionsService.route(
      {
        origin: {
          lat: Number(origin.latitude),
          lng: Number(origin.longitude),
        },
        destination: {
          lat: Number(destination.latitude),
          lng: Number(destination.longitude),
        },
        travelMode: TravelMode.DRIVING,
      },
      (result, status) => {
        const directionsOk = status === "OK" || status === DirectionsStatus.OK;

        if (!directionsOk || !result) {
          resolve(null);
          return;
        }

        const distanceMeters = result.routes?.[0]?.legs?.reduce(
          (total, leg) => total + (leg.distance?.value || 0),
          0,
        );

        resolve(distanceMeters ? distanceMeters / 1000 : null);
      },
    );
  });
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function createDestinationSubRoute(name, lat, lng) {
  return {
    id: 1,
    name,
    lat,
    lng,
    sortableId: `destination-${lat}-${lng}`,
  };
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function isSameRoute(routeA, routeB) {
  return (
    routeA?.name === routeB?.name &&
    routeA?.lat === routeB?.lat &&
    routeA?.lng === routeB?.lng
  );
}

function getBookingRouteValues(subRoutesList) {
  const routes = Array.isArray(subRoutesList)
    ? subRoutesList
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  const destination = routes[routes.length - 1];
  const subRoutes = routes
    .slice(0, -1)
    .filter((route) => !isSameRoute(route, destination))
    .map((route, index) => ({
      id: index + 1,
      name: route.name,
      lat: route.lat,
      lng: route.lng,
    }));

  return {
    destination: getRouteDestination(destination),
    subRoutes,
  };
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "booking-client-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar parada"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const BookingClient = ({ model }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const matchesSM = useMediaQuery(theme.breakpoints.down("sm"));
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [otherPersonName, setOtherPersonName] = useState("");
  const [otherPersonPhone, setOtherPersonPhone] = useState("");
  const [otherPersonNameInput, setOtherPersonNameInput] = useState("");
  const [otherPersonPhoneInput, setOtherPersonPhoneInput] = useState("");
  const [otherPersonModalOpen, setOtherPersonModalOpen] = useState(false);

  const [dateEnd, setDateEnd] = useState(null);
  const [timeEnd, setTimeEnd] = useState(null);
  const [checked, setChecked] = useState(false);
  const handleChangeCheckBox = (event) => setChecked(event.target.checked);
  const [phoneActive, setPhoneActive] = useState(true);
  const changePhoneActive = (e) => setPhoneActive(e.target.checked);
  const [typeCheck, setTypeCheck] = useState(false);
  const changeTypeCheck = (e) => setTypeCheck(e.target.checked);

  useEffect(() => {
    const getListClients = async () => {
      try {
        const token = getTokenRefresh();
        const response = await getClients(token);
        setClients(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    getListClients();
  }, []);

  //traer los precios de firebase
  const [riderRates, setRiderRates] = useState(null);
  useEffect(() => {
    try {
      const dbRefRates = ref(db, `BusinessRate/Price/Arequipa/Rider`);
      get(dbRefRates)
        .then(async (snapshot) => {
          const data = snapshot.val();
          if (data != null) {
            setRiderRates(data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });

  const [addressDestination, setAddressDestination] = useState("");
  const [coordDestination, setCoordDestination] = useState({
    lat: null,
    lng: null,
  });
  const [routeRateMode, setRouteRateMode] = useState("direcciones");

  const [serviceList, setServiceList] = useState([]);
  const [subRoutesList, setSubRoutesList] = useState([]);
  const [subRouteSearch, setSubRouteSearch] = useState("");
  const [subRoutesModalOpen, setSubRoutesModalOpen] = useState(false);
  const [route1, setRoute1] = useState("");
  const [indice, setIndice] = useState(-1);

  const checkoutSchema = yup.object().shape({
    persons: yup.number().required("required"),
    booking: yup.number().required("required"),
    note: yup.string(),
  });

  const initialValues = {
    persons: 1,
    booking: 1,
    note: "",
    price: 0,
    tax: 0,
  };

  const handleOpenOtherPersonModal = () => {
    setOtherPersonNameInput(otherPersonName);
    setOtherPersonPhoneInput(otherPersonPhone);
    setOtherPersonModalOpen(true);
  };

  const handleCloseOtherPersonModal = () => {
    setOtherPersonModalOpen(false);
  };

  const handleAcceptOtherPerson = () => {
    setOtherPersonName(otherPersonNameInput.trim().toUpperCase());
    setOtherPersonPhone(otherPersonPhoneInput.trim());
    setOtherPersonModalOpen(false);
  };

  const handleClearOtherPerson = () => {
    setOtherPersonName("");
    setOtherPersonPhone("");
    setOtherPersonNameInput("");
    setOtherPersonPhoneInput("");
  };

  const destinationInputText =
    subRoutesList.length > 1
      ? `${subRoutesList.length} paraderos`
      : addressDestination;

  useEffect(() => {
    if (subRoutesList.length === 0) {
      return;
    }

    const destination = subRoutesList[subRoutesList.length - 1];
    setAddressDestination(destination.name || "");
    setCoordDestination({
      lat: destination.lat ?? null,
      lng: destination.lng ?? null,
    });
  }, [subRoutesList]);

  const handSelect = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setAddress(value);
    setCoordinates(ll);
  };

  const handSelectRoute = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    const list = [...serviceList];
    list[indice].service = value;
    list[indice].lat = ll.lat;
    list[indice].lng = ll.lng;
    setServiceList(list);
  };

  const handleServiceAdd = () => {
    if (indice === -1) {
      setServiceList([...serviceList, { service: "" }]);
      setIndice(indice + 1);
    } else if (serviceList[indice].service !== "") {
      setServiceList([...serviceList, { service: "" }]);
      setIndice(indice + 1);
      setRoute1("");
    } else {
      window.alert("INGRESE UNA RUTA ANTES DE CREAR OTRO CAMPO");
    }
  };

  const handleServiceRemove = (index) => {
    const list = [...serviceList];
    list.splice(index, 1);
    setServiceList(list);
    setIndice(indice - 1);
  };

  const handleSelectDestination = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setSubRoutesList([createDestinationSubRoute(value, ll.lat, ll.lng)]);
  };

  const handleOpenSubRoutesModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSubRoutesModalOpen(true);
  };

  const handleCloseSubRoutesModal = () => {
    setSubRoutesModalOpen(false);
  };

  const handleSubRouteDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };

  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesList((currentList) => {
      const nextList = currentList.filter(
        (subRoute) => subRoute.sortableId !== sortableId,
      );

      if (nextList.length === 0) {
        setAddressDestination("");
        setCoordDestination({ lat: null, lng: null });
      }

      return nextList;
    });
  };

  const handleSelectSubRoute = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);

    setSubRoutesList((currentList) => [
      ...currentList,
      {
        id: currentList.length + 1,
        name: value,
        lat: ll.lat,
        lng: ll.lng,
        sortableId: `sub-route-${Date.now()}-${ll.lat}-${ll.lng}`,
      },
    ]);
    setSubRouteSearch("");
  };

  const [openOrigin, setOpenOrigin] = useState(false);
  const [isOrigin, setIsOrigin] = useState(false);
  const [originURL, setOriginURL] = useState("");

  const [openDestination, setOpenDestination] = useState(false);
  const [isDestination, setIsDestination] = useState(false);
  const [destinationURL, setDestinationURL] = useState("");

  const handleOpenOrigin = () => setOpenOrigin(true);
  const handleCloseOrigin = () => setOpenOrigin(false);

  const handleOpenDestination = () => setOpenDestination(true);
  const handleCloseDestination = () => setOpenDestination(false);

  const handleOriginAccept = async () => {
    if (originURL) {
      setIsOrigin(true);
      try {
        const getAddress = await getAddressAndLatLng(originURL);
        if (getAddress.data.message === "success") setIsOrigin(false);

        console.log(getAddress?.data);
        setAddress(getAddress?.data?.address);
        setCoordinates({
          lat: getAddress?.data?.lat,
          lng: getAddress?.data?.lng,
        });
        setOpenOrigin(false);
      } catch (error) {
        console.log(error);
      }
    }
    setOpenOrigin(false);
  };

  const handleDestinationAccept = async () => {
    if (destinationURL) {
      setIsDestination(true);
      try {
        const getAddress = await getAddressAndLatLng(destinationURL);
        if (getAddress.data.message === "success") setIsDestination(false);
        console.log(getAddress?.data);
        setAddressDestination(getAddress?.data?.address);
        setCoordDestination({
          lat: getAddress?.data?.lat,
          lng: getAddress?.data?.lng,
        });
        setSubRoutesList([
          createDestinationSubRoute(
            getAddress?.data?.address,
            getAddress?.data?.lat,
            getAddress?.data?.lng,
          ),
        ]);
        setOpenDestination(false);
      } catch (error) {
        console.log(error);
      }
    }
    setOpenDestination(false);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleFormSubmit = async (values) => {
    const routeValues = getBookingRouteValues(subRoutesList);

    if (!client)
      return snackBarDialog("Asigne un Cliente para el pedido", "error");
    if (!coordinates.lat || !coordinates.lng)
      return snackBarDialog("Ingrese el Origen del servicio", "error");
    if (!routeValues.destination?.lat || !routeValues.destination?.lng)
      return snackBarDialog("Ingrese el Destino del servicio", "error");
    if (
      !isSameRoute(
        routeValues.destination,
        subRoutesList[subRoutesList.length - 1],
      )
    )
      return snackBarDialog(
        "El destino debe ser el ultimo paradero",
        "error",
      );

    setAddressDestination(routeValues.destination.name);
    setCoordDestination({
      lat: routeValues.destination.lat,
      lng: routeValues.destination.lng,
    });

    setIsLoading(true);
    const uuid = uuidv4();
    const nDate = new Date(Date.now());

    const data = createDataMySql(values, uuid, nDate, routeValues);
    const backup = await CreateBackupUseCase(data);

    if (backup.message == "success") {
      const orderNumber = backup.insertId;
      const fireData = createDataFirebase(
        values,
        uuid,
        orderNumber,
        nDate,
        routeValues,
      );
      const notification = await createBookingFirebase(fireData);

      if (values.booking != 1 && !checked) {
        await sendEmail(values, orderNumber);
      }

      setAddress("");
      setCoordinates({ lat: null, lng: null });
      setAddressDestination("");
      setCoordDestination({ lat: null, lng: null });
      setServiceList([]);
      setSubRoutesList([]);
      setClient(null);
      setOtherPersonName("");
      setOtherPersonPhone("");
      values.persons = 1;
      values.vehicle = "";
      values.note = "";
      if (notification && !notification.success) {
        snackBarDialog(notification.error, "warning");
      } else if (notification?.data?.failureCount > 0) {
        snackBarDialog(
          `PEDIDO CREADO: ${notification.data.successCount} NOTIFICACIONES ENVIADAS, ${notification.data.failureCount} FALLIDAS`,
          "warning",
        );
      } else if (notification?.data?.totalTokens === 0) {
        snackBarDialog(
          "PEDIDO CREADO, SIN TOKENS DE CONDUCTORES CERCANOS PARA NOTIFICAR",
          "warning",
        );
      } else {
        snackBarDialog("PEDIDO CREADO, ver historial");
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      snackBarDialog("ERROR: NO SE LOGRO CREAR EL SERVICIO", "error");
    }
  };

  const createDataMySql = (values, uuid, nDate, routeValues) => {
    const uid = client.id;
    const destination = routeValues.destination;
    return {
      uuid,
      userType: 1,
      originName: address,
      originLat: coordinates.lat,
      originLng: coordinates.lng,
      originUrl: "",
      destinationName: destination.name,
      destinationLat: destination.lat,
      destinationLng: destination.lng,
      destinationUrl: "",
      otherPersonName: otherPersonName,
      otherPersonPhone: otherPersonPhone,
      subRoutes: routeValues.subRoutes,
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",
      persons: values.persons,
      vehicleModel: vehicleSelect,
      notes: values.note ? values.note.toUpperCase() : "",
      notesAdmin: "",
      userId: uid,
      driverId: 0,
      vehicleId: 0,
      paymentId: 0,
      bookingType: values.booking,
      tourId: values.booking === 4 ? tour.id : 0,
      invoiceId: 0,
      createdBy: "ADMIN",
      status: 1,
      statePayment: "PENDIENTE",
      assigned: false,
      invoice: false,
      invoiceStatus: false,
      travelStatus: 1,
      currency: 1,
      paymentType: 1,
      paymentVoucher: 1,
      tax: values.tax ? values.tax : 0,
      toll: 0,
      price: values.price ? values.price : 0,
      total: priceTotal === "" ? 0 : priceTotal,
      discount: 0,
      advance: 0,
      quote: values.booking != 1 ? !checked : true,
    };
  };

  const createDataFirebase = (values, uuid, counter, nDate, routeValues) => {
    const uid = client.id;
    const destination = routeValues.destination;
    return {
      uuid,
      client: Number(uid),
      clientName: `${client.name} ${client.lastname}`,
      clientEmail: client.email,
      clientPhone: client.phone,
      otherPersonName: otherPersonName,
      otherPersonPhone: otherPersonPhone,
      origin: {
        name: address,
        lat: coordinates.lat,
        lng: coordinates.lng,
      },
      destination: {
        name: destination.name,
        lat: destination.lat,
        lng: destination.lng,
      },
      subRoutes: routeValues.subRoutes,
      intermediate: serviceList,
      persons: values.persons,
      vehicle: vehicleSelect,
      date: values.booking === 1 ? dateFormat(nDate) : dateFormat(date.$d),
      time: values.booking === 1 ? timeFormat(nDate) : timeFormat(time.$d),
      dateEnd: values.booking === 3 ? dateFormat(dateEnd.$d) : "",
      timeEnd: values.booking === 3 ? timeFormat(timeEnd.$d) : "",

      tour_id: values.booking === 4 ? Number(tour.id) : 0,
      tour_description: values.booking === 4 ? tour.code : "",

      note: values.note ? values.note.toUpperCase() : "",
      noteAdmin: values.booking === 4 ? `${tour.code} ${tour.title}` : "",
      currency: 1,
      payment_type: 1,
      payment_voucher: 1,
      tax: values.tax ? Number(values.tax) : 0,
      toll: 0,
      price: values.price ? Number(values.price) : 0,
      total: priceTotal === "" ? 0 : Number(priceTotal),
      discount: 0,
      advance: 0,
      statusService: "PENDIENTE",
      statusPayment:
        Number(values.booking) == 1
          ? "PENDIENTE"
          : checked
            ? "PENDIENTE"
            : "PAGADO",
      // statusPayment: "PENDIENTE", evaluar la cotizacion
      statusBooking: "PENDIENTE",
      createBooking: "ADMIN",
      booking_type: Number(values.booking),
      counterBooking: counter,
      created: Date.now(),
      isCreated: false,
      cotizado: values.booking != 1 ? !checked : true,
      phoneActive: phoneActive,
    };
  };

  const sendEmail = async (values, counter) => {
    try {
      const newBooking = {
        counterBooking: counter,
        clientName: `${client.name} ${client.lastname}`,
        clientEmail: client.email,
        clientPhone: client.phone,
        origin: { name: address },
        destination: { name: addressDestination },
        date: dateFormat(date.$d),
        time: timeFormat(time.$d),
        note: values.note ? values.note.toUpperCase() : "",
        persons: values.persons,
        vehicle: values.vehicle,
        web: "Booking",
      };
      await sendICSEmail("token", newBooking);
    } catch (error) {
      console.log(error);
    }
  };

  const createBookingFirebase = async (data) => {
    const dbRef = `bookingNow/${data.uuid}`;
    await set(ref(db, dbRef), data);

    if (data.booking_type == 1) {
      return await sendBookingRiderNotificationUseCase(data);
    }
    return null;
  };

  const handleMouseDownPassword = (event) => event.preventDefault();

  // TOUR
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const getCountrys = async () => {
      try {
        const response = await getListCountryUseCase();
        setCountries([]);
        setCountries(response.data.data);
      } catch (error) {
        console.log(error);
      }
    };
    getCountrys();
  }, []);

  //SELECCIONA UN PAIS TRAER LISTA DE TOUR
  const [tour, setTour] = useState(null);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    if (country) {
      const getListTour = async () => {
        try {
          console.log(country.id);
          const response = await getListTourAll();
          console.log(response);
          setTours([]);
          setTour(null);
          setTours(response.data.tour);
        } catch (error) {
          console.log(error);
        }
      };
      getListTour();
    }
  }, [country]);

  const [priceTotal, setPriceTotal] = useState(0);
  const [vehicleSelect, setVehicleSelect] = useState(1);
  const [cityRangeRates, setCityRangeRates] = useState([]);

  useEffect(() => {
    const getCityRangeRates = async () => {
      try {
        const rates = await getClientCityRangeListUseCase();
        setCityRangeRates(
          rates
            .filter(
              (rate) =>
                !Number.isNaN(Number(rate.min_km)) &&
                !Number.isNaN(Number(rate.max_km)) &&
                !Number.isNaN(Number(rate.price_km)),
            )
            .sort((a, b) => Number(a.min_km) - Number(b.min_km)),
        );
      } catch (error) {
        console.log(error);
        setCityRangeRates([]);
      }
    };

    getCityRangeRates();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const calculatePrice = async () => {
      if (!coordinates.lat || !coordDestination.lat) {
        return;
      }

      const pointA = {
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      };
      const pointB = {
        latitude: coordDestination.lat,
        longitude: coordDestination.lng,
      };

      if (routeRateMode === "ciudades") {
        try {
          const distanceKm = await getGoogleRouteDistanceKm(pointA, pointB);

          if (isMounted && distanceKm !== null) {
            const precioKmTemp = getPrecioKmTempByDistance(
              distanceKm,
              cityRangeRates,
            );

            if (precioKmTemp !== null) {
              setPriceTotal(roundToNearestFive(distanceKm * precioKmTemp));
            }
          }
        } catch (error) {
          console.log(error);
        }

        return;
      }

      const distance = distanceBetweenCoordinates(pointA, pointB);
      //distance en kilometros
      switch (vehicleSelect) {
        case 1: {
          console.log(riderRates.Sedan);
          const fare = riderRates.Sedan;
          var total = (distance / 1000) * fare.pricePerKM;
          if (total < fare.priceMinimal) total = fare.priceMinimal;
          setPriceTotal(total);
          break;
        }
        case 2: {
          console.log(riderRates.Suv);
          const fare = riderRates.Suv;
          var total = (distance / 1000) * fare.pricePerKM;
          if (total < fare.priceMinimal) total = fare.priceMinimal;
          setPriceTotal(total);
          break;
        }
        case 3: {
          console.log(riderRates.Minivan);
          const fare = riderRates.Minivan;
          var total = (distance / 1000) * fare.pricePerKM;
          if (total < fare.priceMinimal) total = fare.priceMinimal;
          setPriceTotal(total);
          break;
        }
        case 4: {
          console.log(riderRates.Minivan_15);
          const fare = riderRates.Minivan_15;
          var total = (distance / 1000) * fare.pricePerKM;
          if (total < fare.priceMinimal) total = fare.priceMinimal;
          setPriceTotal(total);
          break;
        }
        default: {
          console.log(riderRates.Global);
          const fare = riderRates.Global;
          var total = (distance / 1000) * fare.pricePerKM;
          if (total < fare.priceMinimal) total = fare.priceMinimal;
          setPriceTotal(total);
          break;
        }
      }
      // console.log(riderRates);
    };

    calculatePrice();

    return () => {
      isMounted = false;
    };
  }, [
    coordinates,
    coordDestination,
    routeRateMode,
    vehicleSelect,
    cityRangeRates,
  ]);

  function distanceBetweenCoordinates(pointA, pointB) {
    const earthRadius = 6371;
    const diffLat = toRadians(pointB.latitude - pointA.latitude);
    const diffLng = toRadians(pointB.longitude - pointA.longitude);

    const a =
      Math.sin(diffLat / 2) * Math.sin(diffLat / 2) +
      Math.cos(toRadians(pointA.latitude)) *
        Math.cos(toRadians(pointB.latitude)) *
        Math.sin(diffLng / 2) *
        Math.sin(diffLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = earthRadius * c;
    return distance <= 0.3;
  }

  function toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={checkoutSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box
            onSubmit={handleSubmit}
            backgroundColor={colors.primary[400]}
            p={2}
          >
            <Grid container spacing={1.5} sx={{ alignItems: "center" }}>
              <Grid item xs={12}>
                <FormControl>
                  <RadioGroup
                    row
                    name="routeRateMode"
                    value={routeRateMode}
                    onChange={(event) => setRouteRateMode(event.target.value)}
                  >
                    <FormControlLabel
                      value="direcciones"
                      control={<Radio color="secondary" />}
                      label="Direcciones"
                    />
                    <FormControlLabel
                      value="ciudades"
                      control={<Radio color="secondary" />}
                      label="Ciudades"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Autocomplete
                  id="id-clients"
                  value={client}
                  onChange={(e, newValue) => setClient(newValue)}
                  options={clients}
                  getOptionLabel={(option) =>
                    `${option.name} ${option.lastname} - ${option.email} - ${option.phone}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("LISTA DE CLIENTES")}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            <IconButton
                              size="small"
                              aria-label={t("Agregar cliente")}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenOtherPersonModal();
                              }}
                              sx={{
                                position: "absolute",
                                right: 50,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "text.secondary",
                              }}
                            >
                              <AddIcon fontSize="small" color="success" />
                            </IconButton>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <PlacesAutocomplete
                  value={address}
                  onChange={setAddress}
                  onSelect={handSelect}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <div key={suggestions.description}>
                      <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                          id="outlined-adornment-weight"
                          onChange={handleChange("weight")}
                          endAdornment={
                            address ? (
                              <InputAdornment
                                position="end"
                                onClick={handleServiceAdd}
                              >
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  <AddOutlinedIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : (
                              <InputAdornment
                                position="end"
                                onClick={handleOpenOrigin}
                              >
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  <LinkOutlinedIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }
                          aria-describedby="outlined-weight-helper-text"
                          {...getInputProps({
                            placeholder: t("origin"),
                            className: "location-search-input",
                          })}
                        />
                      </FormControl>
                      <Box
                        key={suggestions.description}
                        className="autocomplete-dropdown-container suggestions"
                      >
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion) => {
                          const className = suggestion.active
                            ? "suggestion-item--active"
                            : "suggestion-item";
                          const style = suggestion.active
                            ? suggestionBackgroundColor(colors.primary[400])
                            : suggestionBackgroundColor(colors.primary[400]);
                          return (
                            <div
                              key={suggestion.description}
                              {...getSuggestionItemProps(suggestion, {
                                className,
                                style,
                              })}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </Box>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Grid>
              <Modal open={openOrigin} onClose={handleCloseOrigin}>
                <Box
                  minWidth={300}
                  sx={{
                    ...boxModalStyle,
                    backgroundColor: colors.primary[500],
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography id="modal-title" variant="h5" component="h2">
                        URL Google Maps
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="modal-description"
                        type="text"
                        name="originURL"
                        value={originURL}
                        onChange={(e) => {
                          setOriginURL(e.target.value);
                        }}
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    <Grid item xs={4} md={2}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleOriginAccept}
                      >
                        aceptar
                      </Button>
                    </Grid>
                    {isOrigin && (
                      <CircularProgress size={24} sx={circularProgressStyle} />
                    )}
                    <Grid item xs={4} md={2}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCloseOrigin}
                      >
                        Cancelar
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Modal>

              <Grid item xs={12} sm={6}>
                <PlacesAutocomplete
                  value={destinationInputText}
                  onChange={(value) => {
                    setAddressDestination(value);
                    if (subRoutesList.length > 0) {
                      setSubRoutesList([]);
                    }
                  }}
                  onSelect={handleSelectDestination}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <div key={suggestions.description}>
                      <FormControl fullWidth variant="outlined">
                        <OutlinedInput
                          id="outlined-adornment-weight"
                          onChange={handleChange("weight")}
                          endAdornment={
                            addressDestination ? (
                              <InputAdornment
                                position="end"
                                onClick={handleOpenSubRoutesModal}
                              >
                                <IconButton
                                  aria-label="abrir paradas"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  <LocationOnIcon />
                                </IconButton>
                              </InputAdornment>
                            ) : (
                              <InputAdornment
                                position="end"
                                onClick={handleOpenDestination}
                              >
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  <LinkOutlinedIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }
                          aria-describedby="outlined-weight-helper-text"
                          {...getInputProps({
                            placeholder: t("destination"),
                            className: "location-search-input",
                          })}
                        />
                      </FormControl>
                      <Box
                        key={suggestions.description}
                        className="autocomplete-dropdown-container suggestionsDestination"
                      >
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion) => {
                          const className = suggestion.active
                            ? "suggestion-item--active"
                            : "suggestion-item";
                          const style = suggestion.active
                            ? suggestionBackgroundColor(colors.primary[500])
                            : suggestionBackgroundColor(colors.primary[300]);
                          return (
                            <div
                              key={suggestion.description}
                              {...getSuggestionItemProps(suggestion, {
                                className,
                                style,
                              })}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </Box>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Grid>
              <Modal
                open={openDestination}
                onClose={handleCloseDestination}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
              >
                <Box
                  minWidth={300}
                  sx={{
                    ...boxModalStyle,
                    backgroundColor: colors.primary[500],
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography id="modal-title" variant="h5" component="h2">
                        URL Google Maps
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="modal-description"
                        type="text"
                        label={t("Destination")}
                        name="destination"
                        value={destinationURL}
                        onChange={(e) => {
                          setDestinationURL(e.target.value);
                        }}
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                    {isDestination && (
                      <CircularProgress size={24} sx={circularProgressStyle} />
                    )}
                    <Grid item xs={4} md={2}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleDestinationAccept}
                      >
                        aceptar
                      </Button>
                    </Grid>
                    <Grid item xs={4} md={2}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCloseDestination}
                      >
                        Cancelar
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Modal>
              <Dialog
                fullWidth
                maxWidth="xs"
                open={subRoutesModalOpen}
                onClose={handleCloseSubRoutesModal}
                PaperProps={{
                  sx: {
                    height: "80vh",
                    maxHeight: "90vh",
                  },
                }}
              >
                <DialogTitle>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                  >
                    <Typography variant="h6" sx={{ flexShrink: 0 }}>
                      {t("Paradas")}
                    </Typography>
                    <PlacesAutocomplete
                      value={subRouteSearch}
                      onChange={setSubRouteSearch}
                      onSelect={handleSelectSubRoute}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            {...getInputProps({
                              label: t("Destino"),
                              placeholder: t("Buscar parada"),
                              className: "location-search-input",
                            })}
                          />
                          <Box className="autocomplete-dropdown-container suggestionsDestination">
                            {loading && <div>Loading...</div>}
                            {suggestions.map((suggestion) => {
                              const className = suggestion.active
                                ? "suggestion-item--active"
                                : "suggestion-item";
                              const style = suggestion.active
                                ? suggestionBackgroundColor(colors.primary[500])
                                : suggestionBackgroundColor(colors.primary[300]);

                              return (
                                <div
                                  key={suggestion.description}
                                  {...getSuggestionItemProps(suggestion, {
                                    className,
                                    style,
                                  })}
                                >
                                  <span>{suggestion.description}</span>
                                </div>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </PlacesAutocomplete>
                  </Stack>
                </DialogTitle>
                <DialogContent>
                  <Stack spacing={1} sx={{ pt: 1 }}>
                    <DragDropProvider onDragEnd={handleSubRouteDragEnd}>
                      <Stack spacing={1}>
                        {subRoutesList.map((subRoute, index) => (
                          <SubRouteSortableItem
                            key={subRoute.sortableId}
                            subRoute={subRoute}
                            index={index}
                            onDelete={handleDeleteSubRoute}
                          />
                        ))}
                      </Stack>
                    </DragDropProvider>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="button"
                    variant="contained"
                    color="error"
                    onClick={handleCloseSubRoutesModal}
                  >
                    {t("Cerrar")}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={handleCloseSubRoutesModal}
                  >
                    {t("Aceptar")}
                  </Button>
                </DialogActions>
              </Dialog>

              <Grid item xs={12} sm={6}>
                <TextField
                  value={values.persons}
                  fullWidth
                  type="number"
                  label={t("persons")}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="persons"
                  error={!!touched.persons && !!errors.persons}
                  helperText={touched.persons && errors.persons}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("vehicle")}</InputLabel>
                  <Select
                    value={vehicleSelect}
                    id="vehicle"
                    name="vehicle"
                    label={t("vehicle")}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      setVehicleSelect(e.target.value);
                    }}
                  >
                    {model.map((model, index) => (
                      <MenuItem key={index} value={model.id}>
                        {t(model.name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={values.booking === 3 ? 4 : 6}
                md={values.booking === 3 ? 4 : 6}
              >
                <FormControl fullWidth>
                  <InputLabel>{t("Para")}</InputLabel>
                  <Select
                    id="booking"
                    value={values.booking}
                    name="booking"
                    label={t("booking")}
                    onChange={handleChange}
                  >
                    <MenuItem value={1}>{t("now")}</MenuItem>
                    <MenuItem value={2}>{t("later")}</MenuItem>
                    <MenuItem value={3}>{t("Por HORA")}</MenuItem>
                    <MenuItem value={4}>{t("TOUR")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={6}
                sm={values.booking === 3 ? 2 : 3}
                sx={
                  {
                    display: {
                      xs:
                        values.booking === 2 ||
                        values.booking === 3 ||
                        values.booking === 4
                          ? "block"
                          : "none",
                      md:
                        values.booking === 2 ||
                        values.booking === 3 ||
                        values.booking === 4
                          ? "block"
                          : "none",
                    },
                  }
                  // displayBox(values.booking, 2, 3)
                }
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={2}>
                    <MobileDatePicker
                      label={values.booking === 3 ? t("startDate") : t("date")}
                      inputFormat="DD/MM/YYYY"
                      name="date"
                      value={date}
                      onChange={(newValue) => {
                        setDate(newValue);
                        setDateEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={values.booking === 3 ? 2 : 3}
                sx={{
                  display: {
                    xs:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                    md:
                      values.booking === 2 ||
                      values.booking === 3 ||
                      values.booking === 4
                        ? "block"
                        : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={3}>
                    <MobileTimePicker
                      label={values.booking === 3 ? t("startTime") : t("time")}
                      value={time}
                      name="time"
                      onChange={(newValue) => {
                        setTime(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={2}
                sx={{
                  display: {
                    xs: values.booking === 3 ? "block" : "none",
                    md: values.booking === 3 ? "block" : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={2}>
                    <MobileDatePicker
                      label={t("dateEnd")}
                      inputFormat="DD/MM/YYYY"
                      name="dateEnd"
                      value={dateEnd}
                      onChange={(newValue) => {
                        setDateEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              <Grid
                item
                xs={6}
                sm={2}
                sx={{
                  display: {
                    xs: values.booking === 3 ? "block" : "none",
                    md: values.booking === 3 ? "block" : "none",
                  },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack spacing={3}>
                    <MobileTimePicker
                      label={t("timeEnd")}
                      value={timeEnd}
                      name="timeEnd"
                      onChange={(newValue) => {
                        setTimeEnd(newValue);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Stack>
                </LocalizationProvider>
              </Grid>

              {countries && (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: {
                      xs: values.booking === 4 ? "block" : "none",
                      md: values.booking === 4 ? "block" : "none",
                    },
                  }}
                >
                  <Autocomplete
                    value={country}
                    disablePortal
                    id="select-origin"
                    onChange={(e, newValue) => {
                      setCountry(newValue);
                    }}
                    options={countries}
                    getOptionLabel={(option) => `${option.name}`}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={t("PAIS")} />
                    )}
                  />
                </Grid>
              )}

              {tours.length > 0 && (
                <Grid
                  item
                  xs={12}
                  sm={8}
                  sx={{
                    display: {
                      xs: values.booking === 4 ? "block" : "none",
                      md: values.booking === 4 ? "block" : "none",
                    },
                  }}
                >
                  <Grid container sx={{ alignItems: "center" }}>
                    <Grid xs={11} padding={1}>
                      <Autocomplete
                        value={tour}
                        disablePortal
                        id="select-origin"
                        onChange={(e, newValue) => {
                          setTour(newValue);
                        }}
                        options={tours}
                        getOptionLabel={(option) =>
                          `${option.code} ${option.title}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <TextField {...params} label={t("TOUR")} />
                        )}
                      />
                    </Grid>
                    {tour && (
                      <Grid xs={1} padding={1}>
                        <spam
                          style={{
                            backgroundColor: "red",
                            color: "white",
                            padding: "5px",
                            borderRadius: "5px",
                            cursor: "pointer",
                            display: "inline-block",
                          }}
                          onClick={() => {
                            const base = `https://tours.taximundo.com`;
                            window.open(
                              `${base}/${
                                tour.language_abbreviation
                              }/${tour.country_name.toLowerCase()}/${tour.city_name.toLowerCase()}/${
                                tour.url_activity
                              }`,
                              "_blank",
                            );
                          }}
                        >
                          Info
                        </spam>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  value={values.note}
                  fullWidth
                  type="text"
                  label={t("notes")}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="note"
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  error={!!touched.note && !!errors.note}
                  helperText={touched.note && errors.note}
                />
              </Grid>

              {!matchesSM && (
                <>
                  <Grid item xs={2} sm={1}>
                    {/* cotizado */}
                    <Checkbox
                      checked={checked}
                      onChange={handleChangeCheckBox}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={2} sm={1}>
                    {/* activar telefono */}
                    <Checkbox
                      checked={phoneActive}
                      onChange={changePhoneActive}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={2} sm={1}>
                    {/* ?? */}
                    <Checkbox
                      checked={typeCheck}
                      onChange={changeTypeCheck}
                      color="success"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={4} sm={2}>
                <TextField
                  value={values.price}
                  fullWidth
                  type="number"
                  label={"Conductor"}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="price"
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <TextField
                  value={values.tax}
                  fullWidth
                  type="number"
                  label={"Impuesto"}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  name="tax"
                />
              </Grid>

              <Grid item xs={4} sm={2}>
                <TextField
                  value={priceTotal}
                  fullWidth
                  type="number"
                  label={"Total"}
                  onBlur={handleBlur}
                  onChange={(e) => setPriceTotal(e.target.value)}
                  name="total"
                />
              </Grid>

              {matchesSM && (
                <Grid item xs={1} sm={1} padding={0}>
                  <Checkbox
                    checked={checked}
                    onChange={handleChangeCheckBox}
                    color="success"
                  />
                </Grid>
              )}

              <Grid item xs={9} sm={3}>
                <Button
                  fullWidth
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{ height: 45 }}
                  disabled={isLoading}
                >
                  <Typography variant="string">
                    &#128196;&nbsp;{" "}
                    {values.booking === 1
                      ? "Pedir Ahora"
                      : t("Guardar Reserva")}{" "}
                    &nbsp; &#128241;&nbsp;&nbsp;&nbsp;&#128179;
                  </Typography>
                </Button>
              </Grid>

              {matchesSM && (
                <>
                  <Grid item xs={1} sm={1} marginLeft={-1}>
                    <Checkbox
                      checked={phoneActive}
                      onChange={changePhoneActive}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={1} sm={1}>
                    <Checkbox
                      checked={typeCheck}
                      onChange={changeTypeCheck}
                      color="success"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
          <SnackBar
            stateSnk={stateSnk}
            vertical={vertical}
            horizontal={horizontal}
            duration={4000}
            handleClose={handleClose}
            severity={severity}
            messageSnk={messageSnk}
          />
          <Dialog
            fullWidth
            maxWidth="xs"
            open={otherPersonModalOpen}
            onClose={handleCloseOtherPersonModal}
          >
            <DialogTitle>{"Pedido para Otra Persona"}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  label={t("Nombre")}
                  value={otherPersonNameInput}
                  onChange={(event) =>
                    setOtherPersonNameInput(event.target.value.toUpperCase())
                  }
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
                <TextField
                  fullWidth
                  label={t("Telefono")}
                  value={otherPersonPhoneInput}
                  onChange={(event) =>
                    setOtherPersonPhoneInput(event.target.value)
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                variant="contained"
                color="error"
                onClick={handleCloseOtherPersonModal}
              >
                {t("Cerrar")}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="warning"
                onClick={handleClearOtherPerson}
              >
                {t("Limpiar")}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleAcceptOtherPerson}
              >
                {t("Aceptar")}
              </Button>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  );
};

export default BookingClient;
